import {
  createAccount as publicCreateAccount,
  earnPoints as publicEarnPoints,
  adjustPoints as publicAdjustPoints,
  getAccount as publicGetAccount,
  queryLoyaltyAccounts as publicQueryLoyaltyAccounts,
  typedQueryLoyaltyAccounts as publicTypedQueryLoyaltyAccounts,
  getProgramTotals as publicGetProgramTotals,
  getCurrentMemberAccount as publicGetCurrentMemberAccount,
  getAccountBySecondaryId as publicGetAccountBySecondaryId,
  listAccounts as publicListAccounts,
  searchAccounts as publicSearchAccounts,
  countAccounts as publicCountAccounts,
  bulkAdjustPoints as publicBulkAdjustPoints,
} from './loyalty-v1-account-accounts.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { HttpClient } from '@wix/sdk-types';
import { createQueryOverloadRouter } from '@wix/sdk-runtime/query-method-router';
import {
  LoyaltyAccountQuery,
  LoyaltyAccountsQueryBuilder,
  typedQueryLoyaltyAccounts as universalTypedQueryLoyaltyAccounts,
} from './loyalty-v1-account-accounts.universal.js';
import { onAccountCreated as publicOnAccountCreated } from './loyalty-v1-account-accounts.public.js';
import { onAccountPointsUpdated as publicOnAccountPointsUpdated } from './loyalty-v1-account-accounts.public.js';
import { onAccountRewardAvailabilityUpdated as publicOnAccountRewardAvailabilityUpdated } from './loyalty-v1-account-accounts.public.js';
import { onAccountUpdated as publicOnAccountUpdated } from './loyalty-v1-account-accounts.public.js';

function customQueryLoyaltyAccounts(httpClient: HttpClient) {
  const router = createQueryOverloadRouter({
    builderQueryFunction: () => publicQueryLoyaltyAccounts(httpClient)(),
    typedQueryFunction: (query: LoyaltyAccountQuery) =>
      publicTypedQueryLoyaltyAccounts(httpClient)(query),
    hasOptionsParameter: false,
  });

  function overloadedQuery(): LoyaltyAccountsQueryBuilder;
  function overloadedQuery(
    query: LoyaltyAccountQuery
  ): ReturnType<typeof universalTypedQueryLoyaltyAccounts>;
  function overloadedQuery(query?: LoyaltyAccountQuery): any {
    return router(...arguments);
  }

  return overloadedQuery;
}

export const createAccount: MaybeContext<
  BuildRESTFunction<typeof publicCreateAccount> & typeof publicCreateAccount
> = /*#__PURE__*/ createRESTModule(publicCreateAccount);
export const earnPoints: MaybeContext<
  BuildRESTFunction<typeof publicEarnPoints> & typeof publicEarnPoints
> = /*#__PURE__*/ createRESTModule(publicEarnPoints);
export const adjustPoints: MaybeContext<
  BuildRESTFunction<typeof publicAdjustPoints> & typeof publicAdjustPoints
> = /*#__PURE__*/ createRESTModule(publicAdjustPoints);
export const getAccount: MaybeContext<
  BuildRESTFunction<typeof publicGetAccount> & typeof publicGetAccount
> = /*#__PURE__*/ createRESTModule(publicGetAccount);
export const getProgramTotals: MaybeContext<
  BuildRESTFunction<typeof publicGetProgramTotals> &
    typeof publicGetProgramTotals
> = /*#__PURE__*/ createRESTModule(publicGetProgramTotals);
export const getCurrentMemberAccount: MaybeContext<
  BuildRESTFunction<typeof publicGetCurrentMemberAccount> &
    typeof publicGetCurrentMemberAccount
> = /*#__PURE__*/ createRESTModule(publicGetCurrentMemberAccount);
export const getAccountBySecondaryId: MaybeContext<
  BuildRESTFunction<typeof publicGetAccountBySecondaryId> &
    typeof publicGetAccountBySecondaryId
> = /*#__PURE__*/ createRESTModule(publicGetAccountBySecondaryId);
export const listAccounts: MaybeContext<
  BuildRESTFunction<typeof publicListAccounts> & typeof publicListAccounts
> = /*#__PURE__*/ createRESTModule(publicListAccounts);
export const searchAccounts: MaybeContext<
  BuildRESTFunction<typeof publicSearchAccounts> & typeof publicSearchAccounts
> = /*#__PURE__*/ createRESTModule(publicSearchAccounts);
export const countAccounts: MaybeContext<
  BuildRESTFunction<typeof publicCountAccounts> & typeof publicCountAccounts
> = /*#__PURE__*/ createRESTModule(publicCountAccounts);
export const bulkAdjustPoints: MaybeContext<
  BuildRESTFunction<typeof publicBulkAdjustPoints> &
    typeof publicBulkAdjustPoints
> = /*#__PURE__*/ createRESTModule(publicBulkAdjustPoints);
export const queryLoyaltyAccounts: MaybeContext<
  BuildRESTFunction<typeof customQueryLoyaltyAccounts> &
    typeof customQueryLoyaltyAccounts
> = /*#__PURE__*/ createRESTModule(customQueryLoyaltyAccounts);
/**
 * Triggered when a loyalty account is created.
 */
export const onAccountCreated: BuildEventDefinition<
  typeof publicOnAccountCreated
> &
  typeof publicOnAccountCreated = createEventModule(publicOnAccountCreated);
/**
 * Triggered when the points of a loyalty account are adjusted, earned, or redeemed.
 */
export const onAccountPointsUpdated: BuildEventDefinition<
  typeof publicOnAccountPointsUpdated
> &
  typeof publicOnAccountPointsUpdated = createEventModule(
  publicOnAccountPointsUpdated
);
/**
 * Triggered when `rewardAvailable` updates on a loyalty account.
 */
export const onAccountRewardAvailabilityUpdated: BuildEventDefinition<
  typeof publicOnAccountRewardAvailabilityUpdated
> &
  typeof publicOnAccountRewardAvailabilityUpdated = createEventModule(
  publicOnAccountRewardAvailabilityUpdated
);
/**
 * Triggered when a loyalty account is updated, including when points are adjusted, earned, or redeemed.
 */
export const onAccountUpdated: BuildEventDefinition<
  typeof publicOnAccountUpdated
> &
  typeof publicOnAccountUpdated = createEventModule(publicOnAccountUpdated);

export {
  UpdateTrigger,
  SortOrder,
  SortType,
  SortDirection,
  MissingValues,
  ScalarType,
  NestedAggregationType,
  DateHistogramAggregationInterval,
  AggregationType,
  Mode,
  Status,
  ProviderName,
  IntervalUnit,
  SubscriptionStatus,
  ReactivationReasonEnum,
  Initiator,
  RefundType,
  UnassignReason,
  ContractSwitchType,
  ContractSwitchReason,
  PriceIncreaseTrigger,
  ProductAdjustment,
  WebhookIdentityType,
} from './loyalty-v1-account-accounts.universal.js';
export {
  LoyaltyAccount,
  Points,
  Tier,
  Contact,
  Image,
  PointsExpiration,
  RewardAvailabilityUpdated,
  CreateAccountRequest,
  CreateAccountResponse,
  EarnPointsRequest,
  EarnPointsRequestActivityDetailsOneOf,
  FollowedSocialMedia,
  EarnPointsResponse,
  PointsUpdated,
  FirstTransaction,
  AdjustPointsRequest,
  AdjustPointsRequestTypeOneOf,
  AdjustPointsResponse,
  DeductPointsRequest,
  DeductPointsResponse,
  RedeemPointsRequest,
  RedeemPointsResponse,
  RedeemDeterminedAmountOfPointsRequest,
  RedeemDeterminedAmountOfPointsResponse,
  RefundTransactionRequest,
  RefundTransactionResponse,
  GetAccountRequest,
  GetAccountResponse,
  QueryLoyaltyAccountsRequest,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Sorting,
  CursorPaging,
  QueryLoyaltyAccountsResponse,
  CursorPagingMetadata,
  Cursors,
  ListUserAccountsRequest,
  ListUserAccountsResponse,
  LoyaltyAccountForMetaSite,
  GetProgramTotalsRequest,
  GetProgramTotalsResponse,
  TierTotal,
  GetCurrentMemberAccountRequest,
  GetCurrentMemberAccountResponse,
  GetAccountBySecondaryIdRequest,
  GetAccountBySecondaryIdRequestIdOneOf,
  GetAccountBySecondaryIdResponse,
  ListAccountsRequest,
  ListAccountsResponse,
  PagingMetadataV2,
  SearchAccountsRequest,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Aggregation,
  AggregationKindOneOf,
  RangeBucket,
  IncludeMissingValuesOptions,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  RangeAggregation,
  ScalarAggregation,
  DateHistogramAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregation,
  GroupByAggregation,
  GroupByAggregationKindOneOf,
  SearchDetails,
  SearchAccountsResponse,
  AggregationData,
  ValueAggregationResult,
  RangeAggregationResult,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  ValueResults,
  RangeResults,
  AggregationResultsScalarResult,
  NestedValueAggregationResult,
  ValueResult,
  RangeResult,
  ScalarResult,
  ScalarDateResult,
  NestedResultValue,
  NestedResultValueResultOneOf,
  Results,
  DateHistogramResult,
  GroupByValueResults,
  DateHistogramResults,
  NestedResults,
  AggregationResultsScalarDateResult,
  AggregationResults,
  AggregationResultsResultOneOf,
  ExportAccountsRequest,
  QueryV2,
  QueryV2PagingMethodOneOf,
  Paging,
  ExportAccountsResponse,
  CountAccountsRequest,
  CountAccountsResponse,
  BulkAdjustPointsRequest,
  BulkAdjustPointsRequestTypeOneOf,
  BulkAdjustPointsResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  Empty,
  TiersRollingUpdate,
  TiersProgramSettingsChanged,
  TiersProgramSettings,
  TiersProgramSettingsPeriodOneOf,
  TierDefinition,
  FocalPoint,
  RollingWindow,
  SubscriptionEvent,
  SubscriptionEventEventOneOf,
  SubscriptionCreated,
  Subscription,
  BillingReference,
  Cycle,
  CycleCycleSelectorOneOf,
  Interval,
  OneTime,
  ReactivationData,
  SubscriptionAssigned,
  SubscriptionCancelled,
  CancellationDetails,
  SubscriptionAutoRenewTurnedOn,
  SubscriptionAutoRenewTurnedOff,
  SubscriptionUnassigned,
  SubscriptionTransferred,
  RecurringChargeSucceeded,
  ContractSwitched,
  ProductPriceIncreaseData,
  SubscriptionNearEndOfPeriod,
  SubscriptionPendingChange,
  RecurringChargeAttemptFailed,
  ContactSyncRequest,
  BulkUpsertAccountsRequest,
  AccountToUpsert,
  BulkUpsertAccountsResponse,
  BulkAccountResult,
  ItemMetadata,
  ApplicationError,
  BulkActionMetadata,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  AccountCreatedEnvelope,
  AccountPointsUpdatedEnvelope,
  AccountRewardAvailabilityUpdatedEnvelope,
  AccountUpdatedEnvelope,
  EarnPointsOptions,
  EarnPointsOptionsActivityDetailsOneOf,
  AdjustPointsOptions,
  AdjustPointsOptionsTypeOneOf,
  LoyaltyAccountsQueryResult,
  LoyaltyAccountsQueryBuilder,
  LoyaltyAccountQuerySpec,
  GetAccountBySecondaryIdOptions,
  GetAccountBySecondaryIdOptionsIdOneOf,
  ListAccountsOptions,
  LoyaltyAccountSearchSpec,
  CountAccountsOptions,
  BulkAdjustPointsOptions,
  BulkAdjustPointsOptionsTypeOneOf,
} from './loyalty-v1-account-accounts.universal.js';
export { utils } from './loyalty-v1-account-accounts.universal.js';
export {
  UpdateTriggerWithLiterals,
  SortOrderWithLiterals,
  SortTypeWithLiterals,
  SortDirectionWithLiterals,
  MissingValuesWithLiterals,
  ScalarTypeWithLiterals,
  NestedAggregationTypeWithLiterals,
  DateHistogramAggregationIntervalWithLiterals,
  AggregationTypeWithLiterals,
  ModeWithLiterals,
  StatusWithLiterals,
  ProviderNameWithLiterals,
  IntervalUnitWithLiterals,
  SubscriptionStatusWithLiterals,
  ReactivationReasonEnumWithLiterals,
  InitiatorWithLiterals,
  RefundTypeWithLiterals,
  UnassignReasonWithLiterals,
  ContractSwitchTypeWithLiterals,
  ContractSwitchReasonWithLiterals,
  PriceIncreaseTriggerWithLiterals,
  ProductAdjustmentWithLiterals,
  WebhookIdentityTypeWithLiterals,
  CommonQueryWithEntityContext,
  LoyaltyAccountQuery,
  CommonSearchWithEntityContext,
  LoyaltyAccountSearch,
} from './loyalty-v1-account-accounts.universal.js';
